import { memo, useMemo, useState, type ReactNode } from 'react';
import { emojiByName } from '@/lib/emoji';
import { highlight, type TokenKind } from '@/lib/highlight';
import { isEmojiOnly, parseMarkdown, type MdNode } from '@/lib/markdown';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { useUi } from '@/stores/ui';
import { useMemberName, useServers } from '@/stores/servers';
import { useUser } from '@/stores/users';
import { CustomEmoji } from './CustomEmoji';

/** Token colours follow the theme, so code stays readable in every light and dark theme. */
const TOKEN_CLASS: Record<TokenKind, string> = {
  keyword: 'text-accent font-semibold',
  string: 'text-success',
  comment: 'text-fg-muted italic',
  number: 'text-warning',
  function: 'text-link',
  type: 'text-accent-2-ink',
  tag: 'text-danger',
  attr: 'text-warning',
  property: 'text-link',
  operator: 'text-fg-muted',
};

/** A code block, highlighted when its language is one we know (js, ts, py, json, css, html, sh, sql, c and friends). */
const CodeBlock = memo(function CodeBlock({ code, lang }: { code: string; lang: string | null }) {
  const tokens = useMemo(() => highlight(code, lang), [code, lang]);
  return (
    <code>
      {tokens.map((t, i) =>
        t.kind ? (
          <span key={i} className={TOKEN_CLASS[t.kind]}>
            {t.text}
          </span>
        ) : (
          t.text
        ),
      )}
    </code>
  );
});

function Mention({ userId, serverId, interactive }: { userId: string; serverId?: string | null; interactive: boolean }) {
  const user = useUser(userId);
  const name = useMemberName(serverId, userId) ?? user?.displayName;
  const isMe = useAuth((s) => s.user?.id === userId);
  const openModal = useUi((s) => s.openModal);
  const className = cn(
    'mention mx-px inline rounded-md px-1 font-semibold transition-colors',
    isMe ? 'bg-accent/20 text-link' : 'bg-link/15 text-link',
    interactive && (isMe ? 'hover:bg-accent hover:text-accent-fg' : 'hover:bg-link hover:text-white'),
  );
  const label = `@${name ?? 'unknown-user'}`;
  if (!interactive) return <span className={className}>{label}</span>;
  return (
    <button type="button" onClick={() => openModal({ type: 'profile', userId, serverId: serverId ?? undefined })} className={className}>
      {label}
    </button>
  );
}

function RoleMention({ roleId }: { roleId: string }) {
  const role = useServers((s) => s.roles[roleId]);
  const mine = useServers((s) => (role ? (s.servers[role.serverId]?.me.roleIds.includes(roleId) ?? false) : false));
  const color = role?.color ?? undefined;
  return (
    <span
      className={cn('mention mx-px inline rounded-md px-1 font-semibold', !color && (mine ? 'bg-accent/20 text-link' : 'bg-link/15 text-link'))}
      style={color ? { color, backgroundColor: `color-mix(in srgb, ${color} 18%, transparent)` } : undefined}
    >
      @{role?.name ?? 'deleted-role'}
    </span>
  );
}

function Spoiler({ children }: { children: ReactNode }) {
  const [shown, setShown] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setShown(true)}
      aria-label={shown ? undefined : 'Reveal spoiler'}
      className={cn('rounded px-0.5 transition-colors', shown ? 'cursor-text bg-hover' : 'cursor-pointer bg-fg-muted text-transparent hover:bg-fg-soft [&_*]:invisible')}
    >
      {children}
    </button>
  );
}

interface RenderContext {
  serverId: string | null | undefined;
  /** Whether "@everyone" pinged (only then is it shown as a mention). */
  everyone: boolean;
  big: boolean;
  /** False inside other controls (e.g. search results), where nested buttons are invalid. */
  interactive: boolean;
  /** Pictures for custom emoji from servers you may not be in (sent with Blaze). */
  emojiUrls?: Record<string, string> | null;
}

function renderNodes(nodes: MdNode[], ctx: RenderContext, keyPrefix = ''): ReactNode[] {
  return nodes.map((node, i) => {
    const key = `${keyPrefix}${i}`;
    switch (node.type) {
      case 'text':
        return <span key={key}>{node.value}</span>;
      case 'bold':
        return <strong key={key} className="font-bold">{renderNodes(node.children, ctx, `${key}-`)}</strong>;
      case 'italic':
        return <em key={key}>{renderNodes(node.children, ctx, `${key}-`)}</em>;
      case 'strike':
        return <s key={key}>{renderNodes(node.children, ctx, `${key}-`)}</s>;
      case 'spoiler':
        return ctx.interactive ? (
          <Spoiler key={key}>{renderNodes(node.children, ctx, `${key}-`)}</Spoiler>
        ) : (
          <span key={key} className="rounded bg-fg-muted px-0.5 text-transparent">{renderNodes(node.children, ctx, `${key}-`)}</span>
        );
      case 'code':
        return (
          <code key={key} className="rounded-md bg-sunken px-1.5 py-0.5 font-mono text-[0.85em] text-fg-soft">
            {node.value}
          </code>
        );
      case 'codeblock':
        return (
          <pre key={key} className="my-1.5 max-w-full overflow-x-auto rounded-xl border border-line bg-sunken p-3 font-mono text-[13px] leading-relaxed text-fg-soft">
            {node.lang && <span className="mb-1 block text-[11px] font-bold tracking-wide text-fg-muted uppercase">{node.lang}</span>}
            <CodeBlock code={node.value} lang={node.lang} />
          </pre>
        );
      case 'quote':
        return (
          <blockquote key={key} className="my-0.5 border-l-4 border-line-strong pl-3 text-fg-soft">
            {renderNodes(node.children, ctx, `${key}-`)}
          </blockquote>
        );
      case 'link':
        return (
          <a key={key} href={node.href} target="_blank" rel="noopener noreferrer nofollow ugc" className="break-all text-link hover:underline">
            {node.href}
          </a>
        );
      case 'mention':
        return <Mention key={key} userId={node.userId} serverId={ctx.serverId} interactive={ctx.interactive} />;
      case 'roleMention':
        return <RoleMention key={key} roleId={node.roleId} />;
      case 'everyone':
        return ctx.everyone ? (
          <span key={key} className="mention mx-px inline rounded-md bg-accent/20 px-1 font-semibold text-link">
            @everyone
          </span>
        ) : (
          <span key={key}>@everyone</span>
        );
      case 'customEmoji':
        return <CustomEmoji key={key} id={node.id} name={node.name} fallbackUrl={ctx.emojiUrls?.[node.id]} className={ctx.big ? 'h-12! w-12!' : undefined} />;
      case 'emoji':
        return (
          <span key={key} title={`:${node.name}:`} className={ctx.big ? 'text-[2.6rem] leading-tight' : undefined}>
            {node.value}
          </span>
        );
      default:
        return null;
    }
  });
}

/** Renders message markdown as React elements — never as raw HTML. */
export const MessageContent = memo(function MessageContent({
  content,
  serverId,
  className,
  suffix,
  interactive = true,
  mentionEveryone = false,
  emojiUrls,
}: {
  content: string;
  /** Pictures for custom emoji from other servers (MessageDTO.emojiUrls). */
  emojiUrls?: Record<string, string> | null;
  serverId?: string | null;
  className?: string;
  /** Rendered inline after the content (e.g. the "(edited)" marker). */
  suffix?: ReactNode;
  /** Set false when rendered inside another control (mentions/spoilers become plain text). */
  interactive?: boolean;
  /** The message's "@everyone" pinged, so it's shown as a mention. */
  mentionEveryone?: boolean;
}) {
  const nodes = useMemo(() => parseMarkdown(content, emojiByName), [content]);
  const big = useMemo(() => isEmojiOnly(nodes), [nodes]);
  return (
    <div data-cf="message-content" className={cn('break-words whitespace-pre-wrap', big && 'text-[2.6rem] leading-tight', className)}>
      {renderNodes(nodes, { serverId, big, interactive, everyone: mentionEveryone, emojiUrls })}
      {suffix}
    </div>
  );
});
